import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../contexts/LangContext';
import { supabase } from '../../lib/supabase';
import AlunaTabBar from '../../components/AlunaTabBar';

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f3eee7',
    padding: '26px 14px 0',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', sans-serif",
    color: '#251c19',
  },
  shell: {
    width: 'min(100%, 430px)',
    minHeight: 'calc(100vh - 26px)',
    margin: '0 auto',
    padding: '28px 22px 230px',
    background: 'linear-gradient(180deg, #fffaf4 0%, #fffaf4 58%, #fff8f1 100%)',
    borderRadius: '44px 44px 0 0',
    boxShadow: '0 28px 80px rgba(66,44,38,.10), inset 0 1px 0 rgba(255,255,255,.95)',
    boxSizing: 'border-box',
    position: 'relative',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    marginBottom: '28px',
  },
  logo: {
    height: '30px',
    width: 'auto',
    objectFit: 'contain',
  },
  profileBtn: {
    height: '42px',
    padding: '0 18px',
    borderRadius: '999px',
    border: '1px solid rgba(216,202,182,.92)',
    background: 'rgba(255,255,255,.58)',
    color: '#7f7165',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  eyebrow: {
    margin: '0 0 8px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    color: '#895b40',
  },
  title: {
    margin: 0,
    fontSize: '32px',
    lineHeight: 1.02,
    fontWeight: 700,
    letterSpacing: 0,
  },
  subtitle: {
    margin: '12px 0 26px',
    color: '#8c7f70',
    fontSize: '15px',
    lineHeight: 1.5,
  },
  hero: {
    display: 'grid',
    gridTemplateColumns: '54px 1fr',
    gap: '14px',
    alignItems: 'center',
    borderRadius: '28px',
    padding: '18px',
    color: '#fffaf4',
    background: 'linear-gradient(135deg, rgba(86,82,76,.96), rgba(37,28,25,.98))',
    border: '1px solid rgba(255,255,255,.28)',
    boxShadow: '0 22px 45px rgba(37,28,25,.16), inset 0 1px 0 rgba(255,255,255,.16)',
  },
  heroAvatar: {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255,255,255,.15)',
    color: '#fffaf4',
    fontSize: '15px',
    fontWeight: 700,
  },
  heroSmall: {
    display: 'block',
    color: 'rgba(255,250,244,.7)',
    fontSize: '13px',
    marginBottom: '2px',
  },
  heroTitle: {
    display: 'block',
    fontSize: '18px',
    lineHeight: 1.16,
    fontWeight: 700,
  },
  heroText: {
    margin: '7px 0 0',
    color: 'rgba(255,250,244,.74)',
    fontSize: '13px',
    lineHeight: 1.42,
  },
  thread: {
    minHeight: 'calc(100vh - 470px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingTop: '30px',
  },
  empty: {
    margin: '46px auto 0',
    width: '86%',
    textAlign: 'center',
    color: '#8c7f70',
    fontSize: '15px',
    lineHeight: 1.55,
  },
  message: {
    maxWidth: '78%',
    padding: '13px 15px 12px',
    borderRadius: '22px',
    boxShadow: '0 14px 30px rgba(82,69,57,.07)',
    fontSize: '14px',
    lineHeight: 1.42,
    wordBreak: 'break-word',
  },
  teamMessage: {
    alignSelf: 'flex-start',
    background: 'rgba(255,255,255,.88)',
    color: '#251c19',
    borderTopLeftRadius: '10px',
  },
  userMessage: {
    alignSelf: 'flex-end',
    background: '#dce8cf',
    color: '#2f3728',
    borderTopRightRadius: '10px',
  },
  messageAuthor: {
    display: 'block',
    marginBottom: '4px',
    color: '#7f7165',
    fontSize: '11px',
    fontWeight: 700,
  },
  messageText: {
    margin: 0,
  },
  messageTime: {
    display: 'block',
    marginTop: '7px',
    textAlign: 'right',
    color: 'rgba(82,69,57,.54)',
    fontSize: '11px',
  },
  composerWrap: {
    position: 'fixed',
    left: '50%',
    bottom: 'max(108px, calc(env(safe-area-inset-bottom, 0px) + 108px))',
    transform: 'translateX(-50%)',
    width: 'min(386px, calc(100% - 44px))',
    zIndex: 190,
    pointerEvents: 'none',
  },
  composer: {
    pointerEvents: 'auto',
    display: 'grid',
    gridTemplateColumns: '1fr 54px',
    alignItems: 'center',
    gap: '10px',
    minHeight: '66px',
    padding: '7px 8px 7px 18px',
    borderRadius: '28px',
    background: 'rgba(255,255,255,.94)',
    border: '1px solid rgba(255,255,255,.92)',
    boxShadow: '0 22px 44px rgba(66,44,38,.13), 0 8px 18px rgba(66,44,38,.07), inset 0 1px 0 rgba(255,255,255,.96)',
    backdropFilter: 'blur(18px)',
  },
  input: {
    width: '100%',
    minHeight: '24px',
    maxHeight: '86px',
    border: 0,
    outline: 0,
    resize: 'none',
    background: 'transparent',
    color: '#251c19',
    fontSize: '15px',
    lineHeight: 1.45,
    fontFamily: 'inherit',
  },
  sendBtn: {
    width: '54px',
    height: '54px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    border: 0,
    background: '#251c19',
    color: '#fffaf4',
    cursor: 'pointer',
    transition: 'opacity .16s ease',
  },
  sendIcon: {
    width: '21px',
    height: '21px',
    marginLeft: '2px',
  },
};

export default function AlunaChat() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const isPt = lang === 'pt';

  useEffect(() => {
    if (!user?.id) return;
    let active = true;

    async function carregarMensagens() {
      const { data } = await supabase
        .from('mensagens')
        .select('*')
        .eq('aluna_id', user.id)
        .order('created_at', { ascending: true });

      if (active) setMensagens(data || []);

      await supabase
        .from('mensagens')
        .update({ lida: true })
        .eq('aluna_id', user.id)
        .eq('remetente', 'admin')
        .eq('lida', false);
    }

    carregarMensagens();
    return () => { active = false; };
  }, [user?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [mensagens]);

  function formatHora(iso) {
    const data = new Date(iso);
    const hoje = new Date();
    const hora = data.toLocaleTimeString(isPt ? 'pt-BR' : 'en-US', { hour: '2-digit', minute: '2-digit' });
    if (data.toDateString() === hoje.toDateString()) return hora;
    return `${data.toLocaleDateString(isPt ? 'pt-BR' : 'en-US', { day: '2-digit', month: '2-digit' })} ${hora}`;
  }

  async function enviarMensagem(event) {
    event.preventDefault();
    const textoLimpo = texto.trim();
    if (!textoLimpo || !user?.id || enviando) return;

    setEnviando(true);
    const { data, error } = await supabase
      .from('mensagens')
      .insert({ aluna_id: user.id, remetente: 'aluna', texto: textoLimpo, lida: false })
      .select()
      .single();

    if (!error && data) setMensagens(atual => [...atual, data]);
    setTexto('');
    setEnviando(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  return (
    <div style={styles.page}>
      <main style={styles.shell}>
        <header style={styles.header}>
          <img src="/logo.svg" alt="Team Carol Macedo" style={styles.logo} />
          <button type="button" style={styles.profileBtn} onClick={() => navigate('/aluna/perfil')}>
            {isPt ? 'Perfil' : 'Profile'}
          </button>
        </header>

        <p style={styles.eyebrow}>Team Carol Macedo</p>
        <h1 style={styles.title}>Chat</h1>
        <p style={styles.subtitle}>
          {isPt ? 'Converse com a equipe pelo seu canal direto.' : 'Talk to the team through your direct channel.'}
        </p>

        <section style={styles.hero}>
          <div style={styles.heroAvatar}>TC</div>
          <div>
            <span style={styles.heroSmall}>Team Carol Macedo</span>
            <strong style={styles.heroTitle}>{isPt ? 'Canal direto com a equipe' : 'Direct channel with the team'}</strong>
            <p style={styles.heroText}>
              {isPt
                ? 'Use para dúvidas do plano, ajustes de rotina e acompanhamento.'
                : 'Use it for plan questions, routine adjustments and follow-up.'}
            </p>
          </div>
        </section>

        <section style={styles.thread} aria-live="polite">
          {mensagens.length === 0 ? (
            <div style={styles.empty}>
              {isPt ? 'Este é seu canal com a equipe. Envie sua primeira dúvida — você pode falar sobre exercícios, dor, aparelhos ou ajustes no plano.' : 'This is your team channel. Send your first question about exercises, pain, equipment or plan adjustments.'}
            </div>
          ) : mensagens.map(msg => {
            const isAluna = msg.remetente === 'aluna';
            return (
              <article
                key={msg.id || `${msg.created_at}-${msg.texto}`}
                style={{ ...styles.message, ...(isAluna ? styles.userMessage : styles.teamMessage) }}
              >
                {!isAluna && <small style={styles.messageAuthor}>Team Carol</small>}
                <p style={styles.messageText}>{msg.texto}</p>
                <time style={styles.messageTime}>{formatHora(msg.created_at)}</time>
              </article>
            );
          })}
          <div ref={bottomRef} />
        </section>
      </main>

      <div style={styles.composerWrap}>
        <form style={styles.composer} onSubmit={enviarMensagem}>
          <textarea
            ref={inputRef}
            value={texto}
            onChange={event => setTexto(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                enviarMensagem(event);
              }
            }}
            placeholder={isPt ? 'Escreva uma mensagem...' : 'Write a message...'}
            rows={1}
            style={styles.input}
          />
          <button
            type="submit"
            disabled={!texto.trim() || enviando}
            style={{ ...styles.sendBtn, opacity: !texto.trim() || enviando ? .42 : 1 }}
            aria-label={isPt ? 'Enviar mensagem' : 'Send message'}
          >
            {enviando ? (
              <span style={{ fontSize: 13, fontWeight: 700 }}>...</span>
            ) : (
              <svg style={styles.sendIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12h13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                <path d="M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </form>
      </div>

      <AlunaTabBar current="chat" />
    </div>
  );
}
